/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Alert,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';

import { StudioEditForm, StudioDeleteDialog } from '../../studios/forms';
import {
  useGetStudioQuery,
  useUpdateStudioMutation,
  useDeleteStudioMutation,
} from '../../../features/studios';
import { StudioFormData } from '../../../types/studioForms';
import { UpdateStudioRequest } from '../../../types/studio';

/**
 * Studio edit page component.
 * Provides a dedicated page for editing existing studios.
 */
const StudioEdit: React.FC = () => {
  const { studioId } = useParams<{ studioId: string }>();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // API hooks
  const {
    data: studio,
    isLoading: isLoadingStudio,
    error: studioError,
  } = useGetStudioQuery(studioId!, { skip: !studioId });
  const [updateStudio, { isLoading: isUpdating, error: updateError }] =
    useUpdateStudioMutation();
  const [deleteStudio, { isLoading: isDeleting, error: deleteError }] =
    useDeleteStudioMutation();

  // Handle form submission
  const handleSubmit = async (formData: Partial<StudioFormData>) => {
    if (!studioId) return;

    try {
      // Convert form data to API request format
      const updateRequest: UpdateStudioRequest = {};

      if (formData.name !== undefined) {
        updateRequest.name = formData.name;
      }
      if (formData.description !== undefined) {
        updateRequest.description = formData.description;
      }
      if (formData.businessInfo !== undefined) {
        updateRequest.businessInfo = formData.businessInfo;
      }

      // Update the studio
      await updateStudio({ id: studioId, data: updateRequest }).unwrap();

      // Handle logo upload if provided
      if (formData.logo) {
        // Logo upload would be handled separately
        console.log('Logo upload would be handled here:', formData.logo);
      }

      // Navigate back to studio
      navigate(`/studio/${studioId}`);
    } catch (submitError) {
      // Error is handled by the mutation and passed to the form
      console.error('Studio update failed:', submitError);
    }
  };

  // Handle form cancellation
  const handleCancel = () => {
    navigate(`/studio/${studioId}`);
  };

  // Handle studio deletion
  const handleDelete = async () => {
    if (!studioId) return;

    try {
      await deleteStudio(studioId).unwrap();
      // Navigate to dashboard after successful deletion
      navigate('/dashboard');
    } catch (deleteSubmitError) {
      // Error is handled by the mutation and passed to the dialog
      console.error('Studio deletion failed:', deleteSubmitError);
    }
  };

  // Get error message for update
  const getUpdateErrorMessage = () => {
    if (updateError) {
      if ('data' in updateError && updateError.data) {
        const errorData = updateError.data as any;
        if (errorData.detail) {
          return errorData.detail;
        }
        if (errorData.name) {
          return `Name: ${errorData.name[0]}`;
        }
        if (errorData.non_field_errors) {
          return errorData.non_field_errors[0];
        }
      }
      return 'Failed to update studio. Please try again.';
    }
    return null;
  };

  // Get error message for delete
  const getDeleteErrorMessage = () => {
    if (deleteError) {
      if ('data' in deleteError && deleteError.data) {
        const errorData = deleteError.data as any;
        if (errorData.detail) {
          return errorData.detail;
        }
        if (errorData.non_field_errors) {
          return errorData.non_field_errors[0];
        }
      }
      return 'Failed to delete studio. Please try again.';
    }
    return null;
  };

  // Show loading state
  if (isLoadingStudio) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress />
          <Typography variant="h6" color="text.secondary">
            Loading studio...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Show error state
  if (studioError || !studio) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {studioError ? 'Failed to load studio.' : 'Studio not found.'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <StudioEditForm
        studio={studio}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        loading={isUpdating}
        error={getUpdateErrorMessage()}
      />

      {/* Delete Dialog */}
      <StudioDeleteDialog
        studio={studio}
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        loading={isDeleting}
        error={getDeleteErrorMessage()}
      />
    </Container>
  );
};

export default StudioEdit;
